import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  Collapse,
  Button,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';

// Material-UI Icons
import BadgeIcon from '@mui/icons-material/Badge';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import WorkIcon from '@mui/icons-material/Work';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import DescriptionIcon from '@mui/icons-material/Description';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import HomeIcon from '@mui/icons-material/Home';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import GavelIcon from '@mui/icons-material/Gavel';
import EngineeringIcon from '@mui/icons-material/Engineering';
import SchoolIcon from '@mui/icons-material/School';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PublicIcon from '@mui/icons-material/Public';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SecurityIcon from '@mui/icons-material/Security';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CategoryIcon from '@mui/icons-material/Category';

// Available icons with labels
export const AVAILABLE_ICONS = [
  { name: 'badge', label: 'ID/Emirates ID', Icon: BadgeIcon },
  { name: 'flight_takeoff', label: 'Visa', Icon: FlightTakeoffIcon },
  { name: 'work', label: 'Labor/Work', Icon: WorkIcon },
  { name: 'medical_services', label: 'Medical', Icon: MedicalServicesIcon },
  { name: 'description', label: 'Documents', Icon: DescriptionIcon },
  { name: 'credit_card', label: 'Payment/Insurance', Icon: CreditCardIcon },
  { name: 'home', label: 'Residence', Icon: HomeIcon },
  { name: 'family_restroom', label: 'Family', Icon: FamilyRestroomIcon },
  { name: 'business_center', label: 'Business', Icon: BusinessCenterIcon },
  { name: 'gavel', label: 'Legal/GDRFA', Icon: GavelIcon },
  { name: 'engineering', label: 'Technical', Icon: EngineeringIcon },
  { name: 'school', label: 'Education/Student', Icon: SchoolIcon },
  { name: 'local_hospital', label: 'Health', Icon: LocalHospitalIcon },
  { name: 'directions_car', label: 'Driving/Transport', Icon: DirectionsCarIcon },
  { name: 'account_balance', label: 'Government/Mohre', Icon: AccountBalanceIcon },
  { name: 'receipt_long', label: 'Certificates', Icon: ReceiptLongIcon },
  { name: 'verified_user', label: 'Verification', Icon: VerifiedUserIcon },
  { name: 'public', label: 'Immigration', Icon: PublicIcon },
  { name: 'apartment', label: 'Housing', Icon: ApartmentIcon },
  { name: 'person_add', label: 'New Employee', Icon: PersonAddIcon },
  { name: 'security', label: 'Security', Icon: SecurityIcon },
  { name: 'assignment', label: 'Assignment', Icon: AssignmentIcon },
  { name: 'folder_shared', label: 'Shared/ICP', Icon: FolderSharedIcon },
  { name: 'local_shipping', label: 'Logistics', Icon: LocalShippingIcon },
  { name: 'attach_money', label: 'Finance', Icon: AttachMoneyIcon },
];

// Color presets
export const COLOR_PRESETS = [
  { name: 'Green', value: '#4CAF50' },
  { name: 'Blue', value: '#2196F3' },
  { name: 'Purple', value: '#9C27B0' },
  { name: 'Orange', value: '#FF9800' },
  { name: 'Red', value: '#FF5722' },
  { name: 'Pink', value: '#E91E63' },
  { name: 'Cyan', value: '#00BCD4' },
  { name: 'Brown', value: '#795548' },
  { name: 'Gray', value: '#607D8B' },
  { name: 'Indigo', value: '#3F51B5' },
];

// Default color
const DEFAULT_COLOR = '#009688'; // Teal (theme color)
const THEME_COLOR = '#009688';

// Styled components
const IconContainer = styled(Box)(({ theme, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 44,
  height: 44,
  borderRadius: 10,
  border: selected ? `2px solid ${THEME_COLOR}` : '2px solid transparent',
  backgroundColor: selected ? `${THEME_COLOR}15` : 'transparent',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: selected ? `${THEME_COLOR}20` : 'rgba(0, 0, 0, 0.04)',
    transform: 'scale(1.08)',
  },
}));

const ColorCircle = styled(Box)(({ theme, selected, color }) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: color,
  border: selected ? '3px solid #212121' : '3px solid transparent',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  boxShadow: selected ? `0 0 0 2px white, 0 0 0 4px ${color}` : '0 2px 4px rgba(0,0,0,0.1)',
  '&:hover': {
    transform: 'scale(1.15)',
    boxShadow: `0 4px 12px ${color}50`,
  },
}));

const PreviewBox = styled(Box)(({ theme, color }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '16px 20px',
  backgroundColor: `${color}10`,
  borderRadius: 12,
  marginTop: 20,
  border: `1px solid ${color}30`,
}));

const PreviewIconBox = styled(Box)(({ color }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 56,
  height: 56,
  borderRadius: 12,
  backgroundColor: color,
  color: 'white',
  boxShadow: `0 4px 12px ${color}40`,
}));

// Collapsed preview card when icon is selected
const CollapsedPreview = styled(Box)(({ theme, color }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 16px',
  backgroundColor: `${color}08`,
  borderRadius: 10,
  border: `1px solid ${color}25`,
  flex: 1,
}));

// Helper function to get icon component by name
export const getIconByName = (iconName) => {
  const iconObj = AVAILABLE_ICONS.find(i => i.name === iconName);
  return iconObj ? iconObj.Icon : CategoryIcon;
};

const IconSelector = ({
  selectedIcon,
  selectedColor,
  onIconChange,
  onColorChange,
  label = 'Icon & Color',
  showPreview = true,
  previewText = '',
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleIconSelect = (iconName) => {
    onIconChange(iconName);
    // Auto-set default color if icon selected but no color yet
    if (!selectedColor) {
      onColorChange(DEFAULT_COLOR);
    }
  };

  const handleColorSelect = (colorValue) => {
    onColorChange(colorValue);
  };

  const handleClear = (e) => {
    e?.stopPropagation();
    onIconChange(null);
    onColorChange(null);
  };

  const handleDone = () => {
    setExpanded(false);
  };

  const SelectedIconComponent = selectedIcon ? getIconByName(selectedIcon) : null;
  const displayColor = selectedColor || DEFAULT_COLOR;
  const selectedIconLabel = AVAILABLE_ICONS.find(i => i.name === selectedIcon)?.label || 'Selected';

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: expanded ? THEME_COLOR : 'divider',
        borderWidth: expanded ? 2 : 1,
        overflow: 'hidden',
        transition: 'all 0.2s ease',
      }}
    >
      {/* Header / Collapsed State */}
      <Box
        sx={{
          p: 2,
          cursor: expanded ? 'default' : 'pointer',
          '&:hover': !expanded ? {
            backgroundColor: 'rgba(0,0,0,0.02)',
          } : {},
        }}
        onClick={() => !expanded && setExpanded(true)}
      >
        {/* Label */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            mb: 1.5,
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontSize: 11,
          }}
        >
          {label} <Typography component="span" variant="caption" color="text.disabled">(Optional)</Typography>
        </Typography>

        {/* Collapsed Content */}
        {!expanded && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {selectedIcon ? (
              // Has selection - show preview card
              <>
                <CollapsedPreview color={displayColor}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      backgroundColor: displayColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 2px 8px ${displayColor}40`,
                    }}
                  >
                    {SelectedIconComponent && (
                      <SelectedIconComponent sx={{ fontSize: 24, color: '#ffffff' }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#212121' }}>
                      {selectedIconLabel}
                    </Typography>
                    <Typography variant="caption" sx={{ color: displayColor }}>
                      {previewText || 'Icon selected'}
                    </Typography>
                  </Box>
                  <Tooltip title="Edit icon">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpanded(true);
                      }}
                      sx={{
                        color: displayColor,
                        backgroundColor: `${displayColor}15`,
                        '&:hover': {
                          backgroundColor: `${displayColor}25`,
                        }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove icon">
                    <IconButton
                      size="small"
                      onClick={handleClear}
                      sx={{
                        color: '#9e9e9e',
                        '&:hover': {
                          color: '#f44336',
                          backgroundColor: '#ffebee',
                        }
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CollapsedPreview>
              </>
            ) : (
              // No selection - show "Add Icon" button
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setExpanded(true)}
                sx={{
                  borderStyle: 'dashed',
                  borderColor: '#bdbdbd',
                  color: '#757575',
                  textTransform: 'none',
                  py: 1.5,
                  px: 3,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: THEME_COLOR,
                    color: THEME_COLOR,
                    backgroundColor: `${THEME_COLOR}08`,
                  }
                }}
              >
                Add Icon & Color
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Expandable content */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          {/* Icon Grid */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Select Icon
            </Typography>
            {selectedIcon && (
              <Chip
                icon={<SelectedIconComponent sx={{ fontSize: 16 }} />}
                label={selectedIconLabel}
                size="small"
                sx={{
                  backgroundColor: `${displayColor}15`,
                  color: displayColor,
                  fontWeight: 500,
                  '& .MuiChip-icon': {
                    color: displayColor,
                  }
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              mb: 3,
              p: 1.5,
              backgroundColor: '#fafafa',
              borderRadius: 2,
            }}
          >
            {AVAILABLE_ICONS.map((iconItem) => {
              const IconComponent = iconItem.Icon;
              const isSelected = selectedIcon === iconItem.name;
              return (
                <Tooltip title={iconItem.label} arrow placement="top" key={iconItem.name}>
                  <IconContainer
                    selected={isSelected}
                    onClick={() => handleIconSelect(iconItem.name)}
                  >
                    <IconComponent
                      sx={{
                        fontSize: 24,
                        color: isSelected ? THEME_COLOR : '#616161',
                      }}
                    />
                  </IconContainer>
                </Tooltip>
              );
            })}
          </Box>

          {/* Color Grid */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
            Select Color
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              flexWrap: 'wrap',
              p: 1.5,
              backgroundColor: '#fafafa',
              borderRadius: 2,
            }}
          >
            {COLOR_PRESETS.map((colorItem) => (
              <Tooltip title={colorItem.name} arrow placement="top" key={colorItem.value}>
                <ColorCircle
                  color={colorItem.value}
                  selected={selectedColor === colorItem.value}
                  onClick={() => handleColorSelect(colorItem.value)}
                />
              </Tooltip>
            ))}
          </Box>

          {/* Preview */}
          {showPreview && selectedIcon && SelectedIconComponent && (
            <PreviewBox color={displayColor}>
              <PreviewIconBox color={displayColor}>
                <SelectedIconComponent sx={{ fontSize: 32, color: '#ffffff' }} />
              </PreviewIconBox>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Preview
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: displayColor }}>
                  {previewText || selectedIconLabel}
                </Typography>
              </Box>
            </PreviewBox>
          )}

          {/* Action Buttons */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.5,
            mt: 3,
            pt: 2,
            borderTop: '1px solid #e0e0e0',
          }}>
            {selectedIcon && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleClear}
                sx={{
                  color: '#757575',
                  borderColor: '#bdbdbd',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#f44336',
                    color: '#f44336',
                    backgroundColor: '#ffebee',
                  }
                }}
              >
                Clear Selection
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={<CheckIcon />}
              onClick={handleDone}
              sx={{
                backgroundColor: THEME_COLOR,
                textTransform: 'none',
                px: 3,
                '&:hover': {
                  backgroundColor: '#00796b',
                }
              }}
            >
              Done
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default IconSelector;
